/**
 * Tidy up: lays the chosen layers out again as a grid, a masonry wall, a column or a
 * row, with the same small gap between every pair. It works on world boxes and answers
 * with how each layer changes, so the canvas turns it into one batch, one undo step.
 *
 * The selection keeps its top-left corner, and the layers keep the order a reader
 * would meet them in (top to bottom, then left to right), so a tidy is a nudge, not a
 * shuffle.
 *
 * Grid and masonry resize what they can (pictures, shapes, groups) so the wall is
 * packed: a grid gives each row one height and fills the same width with every row,
 * masonry gives every layer the column's width. A layer that cannot be resized well (type) keeps its size and is
 * centred where it lands. The column and the row never resize.
 */

export type TidyHow = 'grid' | 'masonry' | 'vertical' | 'horizontal';

export interface TidyBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** False keeps the layer its own size (type, which grows wrong when scaled). Default true. */
  scalable?: boolean;
}

export interface TidyMove {
  id: string;
  /** Uniform scale about the box's top-left corner, applied first. */
  scale: number;
  /** Then how far the (scaled) box's top-left corner moves. */
  dx: number;
  dy: number;
}

/** Menu icons (chrome/icons names), kept here so the canvas menu and the selection bar agree. */
export const TIDY_ICONS = { grid: 'grid', masonry: 'templates', vertical: 'distribute-v', horizontal: 'distribute-h' } as const satisfies Record<TidyHow, string>;

export const TIDY_LAYOUTS: readonly { how: TidyHow; label: string }[] = [
  { how: 'grid', label: 'Grid' },
  { how: 'masonry', label: 'Masonry' },
  { how: 'vertical', label: 'Vertical stack' },
  { how: 'horizontal', label: 'Horizontal row' },
];

const median = (values: readonly number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
};

/**
 * Top to bottom, then left to right. A layer joins a row when it shares at least half
 * of its height (or the row's first layer's, if that is shorter) with that first layer,
 * so ragged tops and mixed heights still read as one row.
 */
export function readingOrder(boxes: readonly TidyBox[]): TidyBox[] {
  const byTop = [...boxes].sort((a, b) => a.y - b.y || a.x - b.x);
  const rows: TidyBox[][] = [];
  for (const box of byTop) {
    const first = rows[rows.length - 1]?.[0];
    const shared = first ? Math.min(first.y + first.height, box.y + box.height) - Math.max(first.y, box.y) : -1;
    if (first && shared >= Math.min(first.height, box.height) / 2) rows[rows.length - 1]!.push(box);
    else rows.push([box]);
  }
  return rows.flatMap((row) => row.sort((a, b) => a.x - b.x));
}

/**
 * One small gap for the whole tidy: 4% of a typical layer's shorter side, never under 4.
 * It ignores how far apart the layers were, so scattered layers come together.
 */
export function tidyGap(boxes: readonly TidyBox[]): number {
  return Math.max(4, Math.round(median(boxes.map((b) => Math.min(b.width, b.height))) * 0.04));
}

/** How many columns a grid or masonry wall of n layers gets: as square as it can be. */
export const tidyColumns = (count: number): number => Math.max(1, Math.ceil(Math.sqrt(count)));

/** Where each box goes, and how big; boxes already in place, at their size, are left out. */
export function tidyUp(boxes: readonly TidyBox[], how: TidyHow, options: { gap?: number; columns?: number } = {}): TidyMove[] {
  const live = boxes.filter((b) => b.width > 0 && b.height > 0);
  if (live.length < 2) return [];
  const gap = options.gap ?? tidyGap(live);
  const left = Math.min(...live.map((b) => b.x));
  const top = Math.min(...live.map((b) => b.y));
  const placed: { box: TidyBox; x: number; y: number; scale: number }[] = [];
  const canScale = (box: TidyBox) => box.scalable !== false;

  if (how === 'horizontal') {
    let x = left;
    for (const box of [...live].sort((a, b) => a.x - b.x || a.y - b.y)) {
      placed.push({ box, x, y: top, scale: 1 });
      x += box.width + gap;
    }
  } else if (how === 'vertical') {
    let y = top;
    for (const box of [...live].sort((a, b) => a.y - b.y || a.x - b.x)) {
      placed.push({ box, x: left, y, scale: 1 });
      y += box.height + gap;
    }
  } else if (how === 'grid') {
    // Rows that fill one width edge to edge: every layer in a row is given the row's
    // height (so pictures of any shape sit side by side with only the gap between), and
    // that height is the one that makes the row exactly as wide as the others. A short
    // last row keeps a typical height and starts at the left, as text does.
    const ordered = readingOrder(live);
    const columns = Math.min(options.columns ?? tidyColumns(ordered.length), ordered.length);
    const width = median(ordered.map((b) => b.width)) * columns + gap * (columns - 1);
    const typical = median(ordered.map((b) => b.height));
    let y = top;
    for (let start = 0; start < ordered.length; start += columns) {
      const row = ordered.slice(start, start + columns);
      const fixed = row.filter((b) => !canScale(b));
      const shape = row.filter(canScale).reduce((sum, b) => sum + b.width / b.height, 0);
      const room = width - gap * (row.length - 1) - fixed.reduce((sum, b) => sum + b.width, 0);
      let height = shape > 0 ? room / shape : Math.max(...row.map((b) => b.height));
      if (row.length < columns && shape > 0) height = Math.min(height, typical);
      if (!(height > 0)) height = typical;
      const tallest = Math.max(height, ...fixed.map((b) => b.height));
      let x = left;
      for (const box of row) {
        const scale = canScale(box) ? height / box.height : 1;
        placed.push({ box, scale, x, y: y + (tallest - box.height * scale) / 2 });
        x += box.width * scale + gap;
      }
      y += tallest + gap;
    }
  } else {
    // Masonry: columns of one width, every layer given that width, each dropped into the
    // shortest column so far. The only space left is the gap.
    const ordered = readingOrder(live);
    const columns = Math.min(options.columns ?? tidyColumns(ordered.length), ordered.length);
    const width = median(ordered.map((b) => b.width));
    const bottoms = Array.from({ length: columns }, () => top);
    for (const box of ordered) {
      let c = 0;
      for (let i = 1; i < columns; i++) if (bottoms[i]! < bottoms[c]! - 0.01) c = i;
      const scale = canScale(box) ? width / box.width : 1;
      const w = box.width * scale;
      placed.push({ box, scale, x: left + c * (width + gap) + (width - w) / 2, y: bottoms[c]! });
      bottoms[c] = bottoms[c]! + box.height * scale + gap;
    }
  }

  return placed
    .map(({ box, x, y, scale }) => ({ id: box.id, scale, dx: x - box.x, dy: y - box.y }))
    .filter(({ dx, dy, scale }) => Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01 || Math.abs(scale - 1) > 1e-4);
}
